import React from 'react';
import { Box, Text, Heading, SimpleGrid, VStack } from '@chakra-ui/react';
import { BinderSectionData } from '../hooks/useBinderSectionData';
import { useBinderCardColors } from '../hooks/useBinderCardColors';

interface ListedVehiclesCardProps {
  sectionData: BinderSectionData;
}

const ListedVehiclesCard: React.FC<ListedVehiclesCardProps> = ({
  sectionData,
}) => {
  const { data } = sectionData;
  const { vehicles = [] } = data || {};
  const colors = useBinderCardColors();

  // Handle case when no vehicles
  if (vehicles.length === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No vehicle information available
        </Text>
      </Box>
    );
  }

  const renderField = (
    label: string,
    value: string,
    isEmpty: boolean = false,
  ) => (
    <VStack align="start" spacing={1}>
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color={colors.labelColor}
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {label}
      </Text>
      <Box>
        {isEmpty || value === 'N/A' ? (
          <Text color="gray.400" fontStyle="italic" fontSize="sm">
            -
          </Text>
        ) : (
          <Text
            fontSize="sm"
            fontWeight="medium"
            color={colors.textColor}
            wordBreak="break-word"
          >
            {value}
          </Text>
        )}
      </Box>
    </VStack>
  );

  return (
    <Box>
      <Box mb={4}>
        <Heading size="sm" color={colors.textColor} mb={2}>
          Listed Vehicles
        </Heading>
        <Text fontSize="sm" color={colors.descriptionColor}>
          {vehicles.length} vehicle(s) listed
        </Text>
      </Box>

      <VStack spacing={6} align="stretch">
        {vehicles.map((vehicle: any, vehicleIndex: number) => (
          <Box
            key={vehicleIndex}
            borderRadius="md"
            borderWidth="1px"
            borderColor={colors.borderColor}
            overflow="hidden"
          >
            {/* Row 1: Basic Info (3 columns) */}
            <Box bg="transparent">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, md: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Year/Make/Model', vehicle.yearMakeModel)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, md: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('VIN', vehicle.vin)}
                </Box>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Class of Use', vehicle.classOfUse)}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Row 2: Business/Principal/Record (3 columns) */}
            <Box bg={colors.emptyFieldBgColor}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, md: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField(
                    'Vehicle in Business Name',
                    vehicle.vehicleInBusinessName,
                  )}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, md: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Principal', vehicle.principal)}
                </Box>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Driving Record', vehicle.drivingRecord)}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Row 3: Garaging Address (1 column) */}
            <Box bg="transparent">
              <Box
                p={4}
                borderBottomWidth="1px"
                borderColor={colors.borderColor}
              >
                {renderField('Garaging Address', vehicle.garagingAddress)}
              </Box>
            </Box>

            {/* Row 4: Purchase Info (4 columns) */}
            <Box bg={colors.emptyFieldBgColor}>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Lienholder', vehicle.lienholder)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Purchase Date', vehicle.purchaseDate)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Purchase Price', vehicle.purchasePrice)}
                </Box>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Minor Conviction', vehicle.minorConviction)}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Row 5: Coverage Types (6 columns) */}
            <Box bg="transparent">
              <SimpleGrid columns={{ base: 1, md: 3, lg: 6 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Rideshare', vehicle.rideshare)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Liability', vehicle.liability)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Specified Perils', vehicle.specifiedPerils)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Comprehensive', vehicle.comprehensive)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Collision', vehicle.collision)}
                </Box>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('All Perils', vehicle.allPerils)}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Row 6: OPCF Coverage (6 columns) */}
            <Box bg={colors.emptyFieldBgColor}>
              <SimpleGrid columns={{ base: 1, md: 3, lg: 6 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Accident', vehicle.accident)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('OPCF 20', vehicle.opcf20)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('OPCF 27', vehicle.opcf27)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('OPCF 43 ●', vehicle.opcf43)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('OPCF 49 ●', vehicle.opcf49)}
                </Box>
                <Box
                  p={4}
                  borderBottomWidth="1px"
                  borderColor={colors.borderColor}
                >
                  {renderField('Winter Tire ●', vehicle.winterTire)}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Row 7: OAB Coverage (6 columns) */}
            <Box bg="transparent">
              <SimpleGrid columns={{ base: 1, md: 3, lg: 6 }} spacing={0}>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderColor={colors.borderColor}
                >
                  {renderField('OAB Medical', vehicle.oabMedical)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderColor={colors.borderColor}
                >
                  {renderField('OAB Income', vehicle.oabIncome)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderColor={colors.borderColor}
                >
                  {renderField('OAB Caregiver', vehicle.oabCaregiver)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderColor={colors.borderColor}
                >
                  {renderField('OAB Dependent', vehicle.oabDependent)}
                </Box>
                <Box
                  p={4}
                  borderRightWidth={{ base: 0, lg: '1px' }}
                  borderColor={colors.borderColor}
                >
                  {renderField('OAB Indexation', vehicle.oabIndexation)}
                </Box>
                <Box p={4}>{renderField('OAB Tort', vehicle.oabTort)}</Box>
              </SimpleGrid>
            </Box>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default ListedVehiclesCard;
